import { Language } from 'intermind';
import { ParticipantRole } from 'mind-sdk-web';
import { MediaStream } from 'mind-sdk-web/lib/MediaStream';
import { Me as MindMe, Participant as MindParticipant } from 'mind-sdk-web/lib/Participant';

export enum ParticipantType {
  REGULAR,
  ME,
  ANONYMOUS,
  PRESENTING,
  MOCK,
}

export const VOICE_ASSISTANT_IDENTIFIER = 'voice assistant';
export const PRESENTING_USER_SUFFIX = '-presenting';

export interface IParticipant {
  getId(): string;
  getName(): string;
  getLanguage(): string;
  getMindParticipant(): MindParticipant | null;
  updateMindParticipant(mindParticipant: MindParticipant): void;
  getMediaStream(): MediaStream | null;
  isStreamingVideo(): boolean;
  isStreamingAudio(): boolean;
  getRole(): (typeof ParticipantRole)[keyof typeof ParticipantRole];
  setRole(role: (typeof ParticipantRole)[keyof typeof ParticipantRole]): void;
  isHandRaised(): boolean;
  setHandRaisedAt(time: number | null): void;
  isModerator(): boolean;
  isMe(): boolean;
  isRegular(): boolean;
  isAnonymous(): boolean;
  isMock(): boolean;
  isPresentingParticipant(): boolean;
  isVoiceAssistant(): boolean;
  isConferenceCreator(): boolean;
  setConferenceCreator(conferenceCreator: boolean): void;
  isPinned(): boolean;
  setPinned(pinned: boolean): void;
  shouldIgnoreVideo(): boolean;
  setIgnoreVideo(ignore: boolean): void;
}

export class Participant implements IParticipant {
  protected mindParticipant?: MindParticipant;
  protected type: ParticipantType;

  protected handRaisedAt: number | null = null;
  protected pinned: boolean = false;
  protected ignoreVideo: boolean = false;
  protected conferenceCreator: boolean = false;

  constructor(mindParticipant?: MindParticipant, type?: ParticipantType) {
    this.mindParticipant = mindParticipant;
    this.type = type ?? ParticipantType.REGULAR;
  }

  getId(): string {
    return this.mindParticipant?.getId() ?? '';
  }

  getName(): string {
    return this.mindParticipant?.getName() ?? '';
  }

  getLanguage(): string {
    return this.mindParticipant?.getLanguage() ?? Language.ENGLISH;
  }

  getMindParticipant(): MindParticipant | null {
    return this.mindParticipant ?? null;
  }

  updateMindParticipant(mindParticipant: MindParticipant): void {
    this.mindParticipant = mindParticipant;
  }

  getMediaStream(): MediaStream | null {
    return this.mindParticipant?.getMediaStream() ?? null;
  }

  isStreamingVideo(): boolean {
    return this.mindParticipant?.isStreamingVideo() ?? false;
  }

  isStreamingAudio(): boolean {
    return this.mindParticipant?.isStreamingAudio() ?? false;
  }

  getRole(): (typeof ParticipantRole)[keyof typeof ParticipantRole] {
    return (
      (this.mindParticipant?.getRole() as (typeof ParticipantRole)[keyof typeof ParticipantRole]) ??
      ParticipantRole.ATTENDEE
    );
  }

  setRole(role: (typeof ParticipantRole)[keyof typeof ParticipantRole]): void {
    this.mindParticipant?.setRole(role);
  }

  isHandRaised(): boolean {
    return this.handRaisedAt !== null;
  }

  getHandRaisedAt(): number | null {
    return this.handRaisedAt;
  }

  setHandRaisedAt(time: number | null): void {
    if (time !== null && time < 0) {
      throw new Error('Hand raised time cannot be negative');
    }
    this.handRaisedAt = time;
  }

  isModerator(): boolean {
    return this.getRole() === ParticipantRole.MODERATOR;
  }

  isMe(): boolean {
    return this.type === ParticipantType.ME;
  }

  isRegular(): boolean {
    return this.type === ParticipantType.REGULAR;
  }

  isAnonymous(): boolean {
    return this.type === ParticipantType.ANONYMOUS;
  }

  isMock(): boolean {
    return this.type === ParticipantType.MOCK;
  }

  isPresentingParticipant(): boolean {
    return this.type === ParticipantType.PRESENTING;
  }

  isVoiceAssistant(): boolean {
    return this.getName().toLowerCase().includes(VOICE_ASSISTANT_IDENTIFIER);
  }

  isConferenceCreator(): boolean {
    return this.conferenceCreator;
  }

  setConferenceCreator(conferenceCreator: boolean): void {
    this.conferenceCreator = conferenceCreator;
  }

  isPinned(): boolean {
    return this.pinned;
  }

  setPinned(pinned: boolean): void {
    this.pinned = pinned;
  }

  shouldIgnoreVideo(): boolean {
    return this.ignoreVideo;
  }

  setIgnoreVideo(ignore: boolean): void {
    this.ignoreVideo = ignore;
  }
}

export class Me extends Participant {
  private videoFloating: boolean = false;

  constructor(readonly mindMe: MindMe) {
    super(mindMe, ParticipantType.ME);
    this.videoFloating = true;
  }

  isVideoFloating(): boolean {
    return this.videoFloating;
  }

  setVideoFloating(value: boolean) {
    this.videoFloating = value;
  }

  private checkWebRtcConnection(): void {
    if (!this.mindMe.session?.getWebRtcConnection()) {
      throw new Error('WebRTC connection is not available');
    }
  }

  setMediaStream(mediaStream: MediaStream | null): void {
    this.checkWebRtcConnection();
    this.mindMe.setMediaStream(mediaStream);
  }

  setSecondaryMediaStream(mediaStream: MediaStream | null): void {
    this.checkWebRtcConnection();
    this.mindMe.setSecondaryMediaStream(mediaStream);
  }
}

export class PresentingParticipant extends Participant {
  constructor(mindParticipant: MindParticipant) {
    super(mindParticipant, ParticipantType.PRESENTING);
    this.pinned = true;
    this.ignoreVideo = false;
  }

  getId(): string {
    return this.mindParticipant?.getId() + PRESENTING_USER_SUFFIX;
  }

  getMediaStream(): MediaStream | null {
    return this.mindParticipant?.getSecondaryMediaStream() ?? null;
  }

  isStreamingAudio(): boolean {
    return this.mindParticipant?.isStreamingSecondaryAudio() ?? false;
  }

  isStreamingVideo(): boolean {
    return this.mindParticipant?.isStreamingSecondaryVideo() ?? false;
  }

  getRole(): (typeof ParticipantRole)[keyof typeof ParticipantRole] {
    return ParticipantRole.ATTENDEE;
  }

  isHandRaised(): boolean {
    return false;
  }

  getHandRaisedAt(): number | null {
    return null;
  }
}

export class MockParticipant extends Participant {
  private readonly _id: string;
  private readonly _name: string;
  private readonly _language: Language;

  constructor(id: string, name: string, language: Language) {
    super(undefined, ParticipantType.MOCK);
    this._id = id;
    this._name = name;
    this._language = language;
    this.pinned = false;
  }

  getId(): string {
    return this._id;
  }

  getName(): string {
    return this._name;
  }

  getLanguage(): Language {
    return this._language;
  }

  getMediaStream(): MediaStream | null {
    return null;
  }

  isStreamingVideo(): boolean {
    return false;
  }

  isStreamingAudio(): boolean {
    return false;
  }

  getRole(): (typeof ParticipantRole)[keyof typeof ParticipantRole] {
    return ParticipantRole.ATTENDEE;
  }
}

/**
 * Class representing an anonymous participant in a conference
 */
export class AnonymousParticipant extends Participant {
  constructor(readonly mindParticipant: MindParticipant) {
    super(mindParticipant, ParticipantType.ANONYMOUS);
  }

  getRole(): (typeof ParticipantRole)[keyof typeof ParticipantRole] {
    return ParticipantRole.ATTENDEE;
  }
}
